// import envConfig from '@/_shared/models/enviroment.model';
import AuthPagesScaffold from '@/components/auth-pages-scaffold/auth-pages-scaffold.component';
import { Button } from '@/components/buttons';
import { Input } from '@/components/inputs';
import Head from 'next/head';
import Link from 'next/link';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useState } from 'react';

import { FIELD_ERROR_MESSAGES } from '@/_shared/constants/errors/form-validation.constants';
import {
  DEFAULT_EMAIL_PATTERN_REGEX,
  ERROR_MESSAGES,
} from '@/_shared/constants';
import { ValidationHelper } from '@/_shared/helpers/validation.helper';
import { EmailRequest } from '@/_shared/_api/models/email.model';
import { EmailService } from '@/_shared/services';
import container from '@/_shared/services/service-container';
import InlineValidation from '@/components/validations/inline-validation.component';
import StandardToast from '@/components/toast/toast.component';
import { ArrowLeft, Mail } from 'lucide-react';
import { NextPageWithScaffoldOption } from '@/types/next-page-with-scaffold-option';

const ForgotPassword: NextPageWithScaffoldOption = () => {
  const emailService = container.get<EmailService>('EmailService');
  const forgotPasswordFormInitialValue = {
    email: '',
  };

  const [showToast, setShowToast] = useState(false);
  const [showErrToast, setShowErrToast] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [contactErrorMessage, setContactErrorMessage] = useState(
    'Failed to saved changes'
  );

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);

    const emailRequest: EmailRequest = {
      recepient: {
        email: (values.email as string).toLowerCase(),
        name: '',
      },
    };
    await emailService
      .sendResetPassword(emailRequest)
      .then((_res) => {
        setShowToast(true);
      })
      .catch((error) => {
        const response = error.response;
        const message = ERROR_MESSAGES[response.data.errorCode]
          ? ERROR_MESSAGES[response.data.errorCode]
          : 'User not found';
        setContactErrorMessage(message);
        setShowErrToast(true);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const showSuccessToast = () => {
    return (
      <StandardToast
        variant="success"
        header="Request sent!"
        message="Please check your email for the instructions."
        className="mb-3"
      ></StandardToast>
    );
  };

  const showErrorToast = () => {
    return (
      <StandardToast
        variant="error"
        message={contactErrorMessage}
      ></StandardToast>
    );
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required(FIELD_ERROR_MESSAGES['required']('Email Address'))
      .matches(
        DEFAULT_EMAIL_PATTERN_REGEX,
        FIELD_ERROR_MESSAGES['email']('Email Address')
      ),
  });

  // This is only needed if you need multiple errror messages to display at the same time,
  // vanilla formik + yup is enough for single message errors
  const validate = (values: any) => {
    setShowToast(false);
    setShowErrToast(false);
    ValidationHelper.validate(values, validationSchema);
  };

  return (
    <>
      <Head>
        <link rel="icon" href="/favicon.ico" />
        <title>MeterCube</title>
        <meta property="og:title" content="Forgot password" key="title" />
      </Head>

      <AuthPagesScaffold>
        <div className="flex justify-center max-sm:items-start max-sm:pt-12 max-sm:p-4 sm:p-10 md:py-16 lg:py-40 w-full h-full">
          <div className="max-sm:w-full w-[400px] flex flex-col justify-center">
            <Link
              href="/login"
              className="text-gray-500 hover:text-gray-600 opacity-90 text-sm active:underline font-semibold flex items-center gap-[10px] mb-8"
            >
              <ArrowLeft />
              <span>Back to Sign in</span>
            </Link>

            <div className="w-full text-start">
              <h1 className="text-3xl font-bold tracking-[0.15px] text-title mb-3">
                Forgot your password?
              </h1>
              <p className="text-sm font-medium text-gray-500">
                No worries! We&apos;ll send you an email with instructions to
                reset your password
              </p>
            </div>

            <Formik
              initialValues={forgotPasswordFormInitialValue}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              validate={validate}
              validateOnBlur={false}
              validateOnChange={false}
            >
              {(formik) => (
                <form
                  className="w-full"
                  noValidate
                  onSubmit={formik.handleSubmit}
                >
                  <div className="w-full mt-8 mb-5">
                    <div>
                      {!showToast && !showErrToast && (
                        <Input
                          disabled={isSubmitting}
                          id="email"
                          name="email"
                          type="email"
                          placeholder="Email"
                          validationError={
                            formik.touched.email && formik.errors.email
                              ? true
                              : false
                          }
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.email}
                          leadingIconLeft={<Mail />}
                        />
                      )}
                      {formik.touched.email && formik.errors.email && (
                        <InlineValidation
                          errors={
                            formik.errors.email
                              ? [formik.errors.email]
                              : ['User does not exist']
                          }
                        ></InlineValidation>
                      )}
                    </div>
                  </div>

                  {!isSubmitting && showToast && showSuccessToast()}
                  {!isSubmitting && showErrToast && showErrorToast()}

                  {!showToast && !showErrToast && (
                    <Button
                      type="submit"
                      className="w-full"
                      isLoading={isSubmitting}
                    >
                      Reset password
                    </Button>
                  )}
                </form>
              )}
            </Formik>
          </div>
        </div>
      </AuthPagesScaffold>
    </>
  );
};

ForgotPassword.hideScaffold = true;

export default ForgotPassword;
